import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { addMonths, format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const money = (n) => (n ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const METHOD_LABELS = { cash: "Cash", card: "Card", venmo: "Venmo", untracked: "Untracked" };

const Chip = ({ label, value, color }) => (
    <span className={`inline-flex items-center gap-1.5 rounded-full border-2 border-monster-navy px-3 py-1.5 text-xs font-bold shadow-sticker-sm ${color}`}>
        {label} <span className="font-display text-sm">{money(value)}</span>
    </span>
);

export const AdminReports = () => {
    const [month, setMonth] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [report, setReport] = useState(null);

    const load = useCallback(() => {
        api.get(`/reports/monthly?month=${format(month, "yyyy-MM")}`)
            .then((res) => setReport(res.data))
            .catch((err) => toast.error(formatApiError(err)));
    }, [month]);

    useEffect(load, [load]);

    const cards = [
        { label: "Cash In", value: report?.income_total ?? 0, bg: "bg-monster-green", testId: "report-income-card" },
        { label: "Bills Out", value: report?.expense_total ?? 0, bg: "bg-monster-red", testId: "report-bills-card" },
        { label: "Net", value: report?.net ?? 0, bg: "bg-monster-navy", testId: "report-net-card" },
    ];

    return (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 100, damping: 15 }} className="space-y-8" data-testid="admin-reports">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-display text-2xl font-semibold text-monster-navy">Monthly Report</h2>
                <div className="flex items-center gap-2">
                    <button data-testid="report-prev-month" onClick={() => setMonth((m) => addMonths(m, -1))} className="rounded-full border-2 border-monster-navy bg-white p-2 shadow-sticker-sm transition-transform hover:-translate-y-0.5" aria-label="Previous month">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span data-testid="report-month-label" className="min-w-[140px] rounded-full border-2 border-monster-navy bg-white px-4 py-2 text-center font-display text-sm font-semibold text-monster-navy shadow-sticker-sm">
                        {format(month, "MMMM yyyy")}
                    </span>
                    <button data-testid="report-next-month" onClick={() => setMonth((m) => addMonths(m, 1))} className="rounded-full border-2 border-monster-navy bg-white p-2 shadow-sticker-sm transition-transform hover:-translate-y-0.5" aria-label="Next month">
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                {cards.map((c) => (
                    <div key={c.label} data-testid={c.testId} className={`rounded-3xl border-2 border-monster-navy p-4 shadow-sticker-sm sm:p-5 ${c.bg}`}>
                        <div className={`font-display text-lg font-semibold sm:text-2xl ${c.bg === "bg-monster-navy" ? (c.value < 0 ? "text-monster-red" : "text-monster-green") : "text-monster-navy"}`}>
                            {c.label === "Net" && c.value < 0 ? `-${money(Math.abs(c.value))}` : money(c.value)}
                        </div>
                        <div className={`text-[10px] font-bold uppercase tracking-wider sm:text-xs ${c.bg === "bg-monster-navy" ? "text-white/60" : "text-monster-navy/60"}`}>{c.label}</div>
                    </div>
                ))}
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
                <div className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
                    <div className="border-b-2 border-monster-navy px-6 py-4">
                        <h3 className="font-display text-lg font-semibold text-monster-navy">Cash flow in</h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {Object.entries(METHOD_LABELS).map(([key, label]) => (
                                <Chip key={key} label={label} value={report?.income_by_method?.[key] ?? 0} color={key === "untracked" ? "bg-monster-cream text-monster-navy/60" : "bg-monster-green/25 text-monster-navy"} />
                            ))}
                        </div>
                    </div>
                    {!report || report.income_entries.length === 0 ? (
                        <p data-testid="report-income-empty" className="p-8 text-center text-sm font-semibold text-monster-navy/50">No income recorded this month.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b-2 border-monster-navy">
                                    <TableHead className="font-display font-semibold">Date</TableHead>
                                    <TableHead className="font-display font-semibold">What</TableHead>
                                    <TableHead className="font-display font-semibold">Method</TableHead>
                                    <TableHead className="font-display font-semibold text-right">Amount</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {report.income_entries.map((e) => (
                                    <TableRow key={e.id} data-testid={`report-income-row-${e.id}`} className="border-b border-monster-navy/10">
                                        <TableCell className="whitespace-nowrap text-xs font-bold">{e.date}</TableCell>
                                        <TableCell className="max-w-[160px] truncate text-xs font-semibold">{e.description || e.category}</TableCell>
                                        <TableCell className="text-xs font-bold capitalize">{e.method ? METHOD_LABELS[e.method] : "—"}</TableCell>
                                        <TableCell className="text-right font-display font-semibold text-monster-green">+{money(e.amount)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>

                <div className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
                    <div className="border-b-2 border-monster-navy px-6 py-4">
                        <h3 className="font-display text-lg font-semibold text-monster-navy">Monthly bills</h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {report && Object.keys(report.expense_by_category).length > 0 ? (
                                Object.entries(report.expense_by_category).map(([cat, val]) => (
                                    <Chip key={cat} label={cat} value={val} color="bg-monster-red/15 text-monster-navy" />
                                ))
                            ) : (
                                <span className="text-xs font-semibold text-monster-navy/40">No bills recorded</span>
                            )}
                        </div>
                        <p data-testid="report-deposits-line" className="mt-3 text-xs font-bold text-monster-navy/60">
                            Deposits collected: <span className="text-monster-green">{money(report?.deposits_total ?? 0)}</span> · Balances collected: <span className="text-monster-green">{money(report?.balances_total ?? 0)}</span>
                        </p>
                    </div>
                    {!report || report.expense_entries.length === 0 ? (
                        <p data-testid="report-bills-empty" className="p-8 text-center text-sm font-semibold text-monster-navy/50">No bills recorded this month.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b-2 border-monster-navy">
                                    <TableHead className="font-display font-semibold">Date</TableHead>
                                    <TableHead className="font-display font-semibold">What</TableHead>
                                    <TableHead className="font-display font-semibold">Category</TableHead>
                                    <TableHead className="font-display font-semibold text-right">Amount</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {report.expense_entries.map((e) => (
                                    <TableRow key={e.id} data-testid={`report-bill-row-${e.id}`} className="border-b border-monster-navy/10">
                                        <TableCell className="whitespace-nowrap text-xs font-bold">{e.date}</TableCell>
                                        <TableCell className="max-w-[160px] truncate text-xs font-semibold">{e.description || "—"}</TableCell>
                                        <TableCell className="text-xs font-bold">{e.category}</TableCell>
                                        <TableCell className="text-right font-display font-semibold text-monster-red">-{money(e.amount)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>

            <div className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
                <div className="border-b-2 border-monster-navy px-6 py-4">
                    <h3 className="font-display text-lg font-semibold text-monster-navy">What was rented</h3>
                </div>
                {!report || report.rentals.length === 0 ? (
                    <p data-testid="report-rentals-empty" className="p-8 text-center text-sm font-semibold text-monster-navy/50">No confirmed rentals this month.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b-2 border-monster-navy">
                                    <TableHead className="font-display font-semibold">Party date</TableHead>
                                    <TableHead className="font-display font-semibold">Customer</TableHead>
                                    <TableHead className="font-display font-semibold">Rented</TableHead>
                                    <TableHead className="font-display font-semibold text-right">Total</TableHead>
                                    <TableHead className="font-display font-semibold">Deposit</TableHead>
                                    <TableHead className="font-display font-semibold">Balance</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {report.rentals.map((r) => (
                                    <TableRow key={r.id} data-testid={`report-rental-row-${r.id}`} className="border-b border-monster-navy/10">
                                        <TableCell className="whitespace-nowrap font-bold">{r.event_date}</TableCell>
                                        <TableCell className="font-semibold">{r.customer}</TableCell>
                                        <TableCell className="font-semibold">{r.inflatable}</TableCell>
                                        <TableCell className="text-right font-display font-semibold">{r.price ? money(r.price) : "—"}</TableCell>
                                        <TableCell>
                                            <span className={`rounded-full border-2 border-monster-navy px-2.5 py-0.5 text-[11px] font-bold ${r.deposit_paid ? "bg-monster-green" : "bg-monster-yellow"}`}>
                                                {r.deposit_paid ? "Paid" : "Due"}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`rounded-full border-2 border-monster-navy px-2.5 py-0.5 text-[11px] font-bold ${r.balance_paid ? "bg-monster-green" : "bg-monster-yellow"}`}>
                                                {r.balance_paid ? "Paid" : "Due"}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
